import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import Switch from "antd/es/switch";
import { sortBy } from "lodash-es";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";
import { AvatarWithName, Labeled } from "@mapmycustomers/ui";

import SelectField from "@app/component/input/SelectField";
import TextWithInfo from "@app/component/typography/TextWithInfo";
import configService from "@app/config/ConfigService";
import { fetchTokenHandoffCode } from "@app/store/app/actions";
import authService from "@app/store/auth/AuthService";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { getLegacyAppUrl } from "@app/util/appUrl";
import { navigateToLegacyApp } from "@app/util/openLegacyApp";

import styles from "./SharingFields.module.scss";

const DEFAULT_OPTION_KEY = -1;
const Option = Select.Option;

interface Props {
  onChange?: (userIds: User["id"][]) => void;
  users: User[];
  value?: User["id"][];
}

const SharingFields = ({
  onChange,
  onFetchTokenHandoffCode,
  users,
  value,
}: Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const sortedUsers = useMemo(() => sortBy(users, [userDisplayName]), [users]);
  const userFilterOption = (inputValue: string, option: any) => {
    const user = users.find((type) => type.id === parseInt(option?.value));
    return ((user?.fullName ?? "") + (user?.username ?? ""))
      .toLowerCase()
      .includes(inputValue.toLowerCase().trim());
  };

  const [userSelectBoxVisible, setUserSelectBoxVisibility] = useState(false);

  const handleSharingChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        onChange?.([]);
      }
      setUserSelectBoxVisibility(checked);
    },
    [onChange]
  );

  const handleUserSelect = useCallback(
    (userIds: User["id"][]) => {
      if (userIds.includes(DEFAULT_OPTION_KEY)) {
        onChange?.(users.map(({ id }) => id));
      } else {
        onChange?.(userIds);
      }
    },
    [onChange, users]
  );

  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();

  if (!token) {
    return null;
  }

  return (
    <>
      <Labeled
        label={
          <TextWithInfo
            info={intl.formatMessage(
              {
                id: "createGroupModal.field.shareWithGroup.tooltipText",
                defaultMessage:
                  "Any records currently in this group, or ones added in the future, will be shared with users selected. You can modify what other users can see in <link>Manage Users</link> ",
                description: "Share With Group field tooltip in the Create Group modal",
              },
              {
                link: (text: ReactNode) => (
                  <a
                    className={styles.manageUsersLink}
                    href={getLegacyAppUrl(baseOldAppUrl, undefined, "/dashboard/teams")}
                    onClick={(event) => {
                      event.preventDefault();
                      navigateToLegacyApp(
                        onFetchTokenHandoffCode,
                        baseOldAppUrl,
                        token,
                        "/dashboard/teams"
                      );
                    }}
                  >
                    {text}
                  </a>
                ),
              }
            )}
          >
            {intl.formatMessage({
              id: "createGroupModal.field.shareWithGroup",
              defaultMessage: "Share records in this group",
              description: "Share With Group field switch label in the Create Group modal",
            })}
          </TextWithInfo>
        }
        labelPosition="side"
        sideLabelSpan={20}
      >
        <Switch checked={userSelectBoxVisible} onChange={handleSharingChange} />
      </Labeled>

      {userSelectBoxVisible && (
        <SelectField<User["id"][]>
          className={styles.selectField}
          dropdownMatchSelectWidth={false}
          filterOption={userFilterOption}
          label={intl.formatMessage({
            id: "createGroupModal.userSelectField.label",
            defaultMessage: "Select users",
            description: "Field label for groups User multi select field",
          })}
          mode="multiple"
          onChange={handleUserSelect}
          value={value}
        >
          <Option key={DEFAULT_OPTION_KEY} value={DEFAULT_OPTION_KEY}>
            {intl.formatMessage({
              id: "createGroupModal.userSelectField.allUsers",
              defaultMessage: "Select all users in list",
              description: "All Users option name for groups User multi select field",
            })}
          </Option>
          )
          {sortedUsers.map((user) => (
            <Option key={user.id} label={userDisplayName(user)} value={user.id}>
              <AvatarWithName avatarClassName={styles.avatar} user={user} />
            </Option>
          ))}
        </SelectField>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  users: getUsers(state),
});

const mapDispatchToProps = {
  onFetchTokenHandoffCode: fetchTokenHandoffCode,
};

export default connect(mapStateToProps, mapDispatchToProps)(SharingFields);
